import CustomModal from 'components/custom-modal/CustomModal';
import React, { useEffect, useState } from 'react';
import CustomSwitch from 'components/switch/Switch';
import { FormCloseIcon } from 'assets/icons';
import { CustomButton } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableItem from 'components/customize-columns-modal/SortableItem';

export interface ColumnConfig {
  columnKey: string;
  columnLabel: string;
  isEnabled: boolean;
  priority: number;
  showAlways: boolean;
}

interface EditTableModalProps {
  onClose: () => void;
  onSave: (groupByStatus: boolean, columns: ColumnConfig[]) => void;
  isSaving: boolean;
  initialGroupByStatus: boolean;
  columns: ColumnConfig[];
  reorderableStartIndex?: number;
}

const EditTableModal: React.FC<EditTableModalProps> = ({
  onClose,
  onSave,
  isSaving,
  initialGroupByStatus = false,
  columns,
  reorderableStartIndex = 1
}) => {
  const [groupByStatus, setGroupByStatus] = useState(initialGroupByStatus);
  const [items, setItems] = useState<ColumnConfig[]>(columns);

  useEffect(() => {
    setItems(columns);
  }, [columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    })
  );

  const handleToggleGroupByStatus = () => {
    setGroupByStatus(prev => !prev);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItems(prevItems => {
        const oldIndex = prevItems.findIndex(item => item.columnKey === active.id);
        const newIndex = prevItems.findIndex(item => item.columnKey === over.id);

        // Prevent dragging to positions before reorderableStartIndex
        if (newIndex < reorderableStartIndex) {
          return prevItems;
        }

        return arrayMove(prevItems, oldIndex, newIndex);
      });
    }
  };

  const toggleColumnSelection = (columnKey: string) => {
    setItems(prevItems => {
      return prevItems.map(item => (item.columnKey === columnKey ? { ...item, isEnabled: !item.isEnabled } : item));
    });
  };

  const handleSave = () => {
    const itemsWithPriorityOrder = items.map((item, index) => ({
      ...item,
      priority: index + 1
    }));

    onSave(groupByStatus, itemsWithPriorityOrder);
  };

  const isItemReorderable = (index: number) => index >= reorderableStartIndex;

  return (
    <CustomModal open={true} closeModal={onClose} width="md:w-[488px]" className="p-0">
      <div className="flex w-full flex-col">
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <h2 className="text-xl font-bold text-heading">Edit table</h2>
          <button
            aria-label="Close modal"
            tabIndex={0}
            onClick={onClose}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onClose()}
            className="text-gray-400 hover:text-gray-600 focus:outline-none">
            <FormCloseIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="px-6">
          <div className="flex items-center justify-between rounded-md bg-[#F7F9FB] px-4 py-3">
            <span className="text-sbase font-semibold text-primaryText">Group by status</span>
            <CustomSwitch
              enabled={groupByStatus}
              toggleSwitch={handleToggleGroupByStatus}
              id="group-by-status-switch"
              className="ml-2"
            />
          </div>

          {items.length > 0 && (
            <div className="mb-6">
              <div className="my-4 border-b border-highlightGray" />
              <h3 className="mb-2 text-sbase font-bold text-heading">Column view</h3>
              <div
                className="customNormalScroll relative max-h-[52vh] overflow-y-auto overflow-x-clip"
                id="columns-container">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                  modifiers={[restrictToParentElement, restrictToVerticalAxis]}>
                  <SortableContext items={items.map(item => item.columnKey)} strategy={verticalListSortingStrategy}>
                    {items.map((column, index) => {
                      const canReorder = isItemReorderable(index);
                      return (
                        <SortableItem
                          key={column.columnKey}
                          column={column}
                          toggleColumnSelection={toggleColumnSelection}
                          canReorder={canReorder}
                          isFirst={index === 0}
                          isLast={index === items.length - 1}
                        />
                      );
                    })}
                  </SortableContext>
                </DndContext>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3 px-6 pb-6">
          <CustomButton
            id="cancel-edit-table"
            type={ButtonType.SECONDARY}
            onClick={onClose}
            className="h-10 min-w-[100px]">
            <span className="text-base font-semibold">Cancel</span>
          </CustomButton>
          <CustomButton
            id="save-edit-table"
            type={ButtonType.PRIMARY}
            onClick={handleSave}
            className="h-10 min-w-[100px]"
            isLoading={isSaving}>
            <span className="text-base font-semibold">Save</span>
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default EditTableModal;
